import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Plug, Loader2, Plus, Trash2, Power, Activity, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const EnterpriseIntegrationsPage = () => {
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(true);
  const [catalog, setCatalog] = useState([]);
  const [connections, setConnections] = useState([]);
  const [provider, setProvider] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const cat = await fetch(`${API_URL}/api/enterprise-integrations/catalog`, { headers: authHeaders() });
      if (cat.status === 403) { setAvailable(false); return; }
      if (!cat.ok) throw new Error('load failed');
      const c = (await cat.json()).connectors || [];
      setCatalog(c);
      if (!provider && c.length) setProvider(c[0].provider);
      const conns = await fetch(`${API_URL}/api/enterprise-integrations/connections`, { headers: authHeaders() });
      setConnections((await conns.json()).connections || []);
      setAvailable(true);
    } catch { /* keep */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const register = async (e) => {
    e.preventDefault(); setMsg('');
    const res = await fetch(`${API_URL}/api/enterprise-integrations/connections`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ provider, display_name: displayName, config_summary: {} }),
    });
    if (res.status === 403) { setMsg('Registering a connection requires Super Admin.'); return; }
    if (!res.ok) { const d = await res.json().catch(() => ({})); setMsg(d.detail || 'Register failed'); return; }
    setDisplayName(''); setMsg('Connection registered (pending, disabled by default).'); load();
  };

  const act = async (c, path, method = 'PATCH') => {
    const res = await fetch(`${API_URL}/api/enterprise-integrations/connections/${c.id}${path}`, { method, headers: authHeaders() });
    if (res.status === 403) { setMsg('This action requires Super Admin.'); return; }
    if (path === '/test' && res.ok) { const d = await res.json(); setMsg(d.message); }
    load();
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="enterprise-integrations-page">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Plug className="w-7 h-7 text-violet-600" /> Enterprise Integrations
          </h1>
          <p className="text-gray-600 mt-1">
            Register and manage enterprise connectors. Every provider is mocked — nothing connects to a live third party, and each connection is human-approved.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12" data-testid="enterprise-integrations-loading"><Loader2 className="w-8 h-8 animate-spin text-violet-600" /></div>
        ) : !available ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6" data-testid="enterprise-integrations-unavailable">
            <p className="text-gray-700">Enterprise Integrations is not currently available.</p>
          </div>
        ) : (
          <>
            {msg && <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 break-words" data-testid="enterprise-integrations-msg">{msg}</div>}

            <div className="flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-lg p-3 text-violet-800 text-sm" data-testid="enterprise-integrations-banner">
              <Info className="w-5 h-5" /> All connectors are mocked. No live third-party calls, no credentials stored. Connections stay disabled until a Super Admin approves.
            </div>

            <div data-testid="enterprise-integrations-catalog" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {catalog.map((c) => (
                <div key={c.provider} data-testid={`catalog-${c.provider}`} className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">{c.name}</span>
                    <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{c.category}</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{c.description}</p>
                  <span className="text-[10px] uppercase text-violet-500">mocked</span>
                </div>
              ))}
            </div>

            <form onSubmit={register} className="bg-white border border-gray-200 rounded-xl p-5 space-y-3" data-testid="enterprise-integrations-register-form">
              <h2 className="font-semibold text-gray-900">Register Connection</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <select value={provider} onChange={(e) => setProvider(e.target.value)} data-testid="ei-provider-select" className="border rounded-lg px-3 py-2 text-sm">
                  {catalog.map((c) => <option key={c.provider} value={c.provider}>{c.name}</option>)}
                </select>
                <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Display name (optional)" data-testid="ei-displayname-input" className="border rounded-lg px-3 py-2 text-sm" />
              </div>
              <button type="submit" data-testid="ei-register-submit" className="inline-flex items-center gap-2 bg-violet-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-violet-700 transition-colors">
                <Plus className="w-4 h-4" /> Register
              </button>
            </form>

            <div className="space-y-2" data-testid="enterprise-integrations-connections">
              <h2 className="font-semibold text-gray-900">Connections ({connections.length})</h2>
              {connections.length === 0 ? (
                <div className="text-center py-8 text-gray-400" data-testid="ei-empty">No connections registered.</div>
              ) : connections.map((c) => (
                <div key={c.id} data-testid={`ei-connection-${c.id}`} className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{c.display_name}</span>
                        <span data-testid={`ei-status-${c.id}`} className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${c.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {c.enabled ? 'enabled' : c.status}
                        </span>
                        <span className="text-[10px] uppercase text-gray-400">{c.provider}</span>
                        <span className="text-[10px] uppercase text-violet-500">mocked</span>
                      </div>
                      {c.last_test && <p className="text-xs text-gray-400 mt-1">last test: {c.last_test.message}</p>}
                    </div>
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <button onClick={() => act(c, '/test', 'POST')} data-testid={`ei-test-${c.id}`} className="inline-flex items-center gap-1 text-xs text-violet-600 hover:underline"><Activity className="w-4 h-4" /> Test</button>
                      {c.enabled
                        ? <button onClick={() => act(c, '/disable')} data-testid={`ei-disable-${c.id}`} className="inline-flex items-center gap-1 text-xs text-gray-700 hover:underline"><Power className="w-4 h-4" /> Disable</button>
                        : <button onClick={() => act(c, '/approve')} data-testid={`ei-approve-${c.id}`} className="inline-flex items-center gap-1 text-xs text-green-600 hover:underline"><Power className="w-4 h-4" /> Approve</button>}
                      <button onClick={() => act(c, '', 'DELETE')} data-testid={`ei-delete-${c.id}`} className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline"><Trash2 className="w-4 h-4" /> Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default EnterpriseIntegrationsPage;
